"use client";

import Surface from "@/components/_shared/controls/Surface";
import { QueryKeys } from "@/data/globals";
import { CmsItemMarkdown } from "@/prisma/generated/browser";
import { Box, Button, CircularProgress, Divider, Stack, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { throttle } from "lodash-es";
import { marked } from "marked";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useState } from "react";
import Editor from "react-simple-code-editor";

type Props = {
  selectedItem: CmsItemMarkdown;
  inlineEditor?: boolean;
  onSaved?: (item: CmsItemMarkdown) => void;
  onDeleted?: (itemId: string) => void;
};

const getNewItem = () => ({
  id: nanoid(12),
  key: "",
  content: ""
});

export default function CmsItemMarkdownEditorView({ selectedItem, inlineEditor, onSaved, onDeleted }: Props) {
  const queryClient = useQueryClient();
  const [item, setItem] = useState<CmsItemMarkdown>(getNewItem());
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState("");

  useEffect(() => {
    console.log("Selected item changed:", selectedItem);
    if (selectedItem) {
      setItem(selectedItem);
    } else {
      setItem(getNewItem());
    }
    setDirty(false);
  }, [selectedItem]);

  const updatePreview = useCallback(
    throttle(
      async (content: string) => {
        const parsed = await marked.parse(content);
        setPreview(parsed);
      },
      1000,
      { leading: false }
    ),
    []
  );

  useEffect(() => {
    void updatePreview(item.content || "");
  }, [item.content, updatePreview]);

  useEffect(() => {
    return () => {
      updatePreview.cancel();
    };
  }, [updatePreview]);

  const saveMutation = useMutation({
    mutationFn: async (data: CmsItemMarkdown) => {
      const response = await fetch(`/api/admin/cms/items-markdown/${data.id}`, {
        method: "POST",
        body: JSON.stringify({
          ...data,
          content: data.content
        })
      });
      if (!response.ok) throw new Error("Failed to save");
      return response.json() as Promise<CmsItemMarkdown>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CMS_ITEMS_MARKDOWN] });
      queryClient.setQueryData([QueryKeys.CMS_ITEMS_MARKDOWN, data.id], data);
      setDirty(false);
      onSaved?.(data);
    }
  });
  const deleteMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const response = await fetch(`/api/admin/cms/items-markdown/${itemId}`, {
        method: "DELETE"
      });
      if (!response.ok) throw new Error("Failed to delete");
      return response.json();
    },
    onSuccess: (_, itemId) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CMS_ITEMS_MARKDOWN] });
      setItem(getNewItem());
      setDirty(false);
      onDeleted?.(itemId);
    }
  });

  const updateItem = async () => {
    if (!item.key.trim()) {
      alert("Item key is required");
      return;
    }

    try {
      await saveMutation.mutateAsync(item);
    } catch (error) {
      console.error("Error saving item:", error);
      alert("Failed to save item");
    }
  };

  const deleteItem = async () => {
    if (!item.id) return;
    if (!confirm(`Are you sure you want to delete ${item.key}?`)) return;

    try {
      await deleteMutation.mutateAsync(item.id);
    } catch (error) {
      console.error("Error deleting item:", error);
      alert("Failed to delete item");
    }
  };

  const saving = saveMutation.isPending || deleteMutation.isPending;

  return (
    <Surface>
      <Stack direction="row" sx={{ p: 2 }} spacing={2}>
        <TextField
          size="small"
          label="Item Key"
          value={item.key}
          onChange={(e) => {
            setItem((x) => ({ ...x, key: e.target.value }));
            setDirty(true);
          }}
          sx={{ flex: 1 }}
          disabled={saving || inlineEditor}
        />
        {saving ? (
          <CircularProgress size={32} sx={{ alignSelf: "center" }} />
        ) : (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={() => updateItem()}
              disabled={!dirty || !item.key.trim()}
              sx={{ ml: 2 }}
            >
              Save
            </Button>
            {item.key && (
              <Button variant="outlined" color="error" onClick={() => deleteItem()}>
                Delete
              </Button>
            )}
          </Stack>
        )}
      </Stack>
      <Divider />
      <div style={{ height: "600px", overflowY: "scroll" }}>
        <Editor
          value={item.content || ""}
          onValueChange={(content) => {
            setItem((current) => ({ ...current, content }));
            setDirty(true);
          }}
          highlight={(code) => code}
          padding={8}
          className="code-editor"
          preClassName="code-editor"
          textareaClassName="code-editor"
        />
      </div>
      <p style={{ padding: "0.5rem" }}>Preview:</p>
      <Box
        sx={{
          p: 2,
          pt: 0
        }}
      >
        <div className="cms-content" dangerouslySetInnerHTML={{ __html: preview }} />
      </Box>
    </Surface>
  );
}
